import { cn } from "@/utils/cn";
import { Network } from "lucide-react";
import { useMemo, useState } from "react";
import type { SubgraphNode, SubgraphResponse } from "../types";

const W = 960;
const H = 640;
const CX = W / 2;
const CY = H / 2;

const COLOR_IN = "#2563eb"; // citers (incoming)
const COLOR_OUT = "#059669"; // references (outgoing)
const COLOR_CENTER = "#16243d";
const COLOR_CROSS = "#a1a1aa";

type LayoutMode = "semantic" | "radial";

interface PlacedNode {
  node: SubgraphNode;
  side: "in" | "out";
  x: number;
  y: number;
  r: number;
  /** False when the node has no abstract embedding (semantic mode only):
   * it is parked on the outer ring instead of the semantic map. */
  embedded: boolean;
}

/** Radius from citation count: 5px floor, 14px at ~2000+. */
const nodeRadius = (count: number) =>
  5 + 9 * Math.sqrt(Math.min(count, 2000) / 2000);

/** Best re-query id for a node (matches backend _graph_key formats). */
export const subgraphNodeId = (node: SubgraphNode): string | null => {
  if (node.arxiv) return `arx_${node.arxiv}`;
  if (node.doi) return node.doi;
  if (node.pmid) return node.pmid;
  return null;
};

const truncate = (s: string, n: number) =>
  s.length > n ? `${s.slice(0, n - 1)}…` : s;

/** Angle (radians) for the i-th of n nodes on one side's arc. */
const sideAngle = (side: "in" | "out", i: number, n: number) => {
  const start = side === "in" ? (105 * Math.PI) / 180 : (-75 * Math.PI) / 180;
  const end = side === "in" ? (255 * Math.PI) / 180 : (75 * Math.PI) / 180;
  const t = n <= 1 ? 0.5 : i / (n - 1);
  return start + t * (end - start);
};

/** Spread nodes along a vertical arc on one side of the center. Nodes
 * alternate between two radii when crowded so labels don't collide. */
const placeRadial = (
  nodes: SubgraphNode[],
  side: "in" | "out",
): PlacedNode[] => {
  const staggered = nodes.length > 12;
  return nodes.map((node, i) => {
    const angle = sideAngle(side, i, nodes.length);
    const radius = staggered && i % 2 === 1 ? 292 : 232;
    return {
      node,
      side,
      x: CX + radius * Math.cos(angle),
      y: CY + radius * Math.sin(angle),
      r: nodeRadius(node.count),
      embedded: true,
    };
  });
};

/**
 * Semantic layout: nodes with embeddings sit at their (uniformly scaled)
 * PCA coordinates, so on-screen distance tracks cosine distance between
 * abstracts. Nodes without embeddings are parked on the outer ring at
 * their radial angle. A short overlap-relaxation pass keeps close
 * neighbors legible without destroying the geometry.
 */
const placeSemantic = (
  incoming: SubgraphNode[],
  outgoing: SubgraphNode[],
  coords: Record<string, [number, number]>,
  centerId: number,
): { placed: PlacedNode[]; center: { x: number; y: number } } => {
  const padX = 150;
  const padY = 60;
  const all = [
    ...incoming.map((n) => ({ n, side: "in" as const })),
    ...outgoing.map((n) => ({ n, side: "out" as const })),
  ];
  const embeddedPts = all
    .filter(({ n }) => coords[String(n.id)])
    .map(({ n }) => coords[String(n.id)]);
  if (coords[String(centerId)]) embeddedPts.push(coords[String(centerId)]);
  const maxAbsX = Math.max(0.05, ...embeddedPts.map((p) => Math.abs(p[0])));
  const maxAbsY = Math.max(0.05, ...embeddedPts.map((p) => Math.abs(p[1])));
  // One shared scale factor keeps the map isotropic (distances honest).
  const scale = Math.min((W / 2 - padX) / maxAbsX, (H / 2 - padY) / maxAbsY);

  const project = (p: [number, number]) => ({
    x: CX + p[0] * scale,
    y: CY + p[1] * scale,
  });

  const placed: PlacedNode[] = all.map(({ n, side }, i) => {
    const c = coords[String(n.id)];
    if (c) {
      const { x, y } = project(c);
      return { node: n, side, x, y, r: nodeRadius(n.count), embedded: true };
    }
    const angle = sideAngle(side, i, all.length);
    return {
      node: n,
      side,
      x: CX + 296 * Math.cos(angle),
      y: CY + 296 * Math.sin(angle),
      r: nodeRadius(n.count),
      embedded: false,
    };
  });

  const centerCoord = coords[String(centerId)];
  const center = centerCoord ? project(centerCoord) : { x: CX, y: CY };

  // Overlap relaxation: push apart pairs closer than their radii allow.
  // The center stays fixed so it remains the semantic anchor.
  for (let iter = 0; iter < 60; iter++) {
    let moved = false;
    for (let a = 0; a < placed.length; a++) {
      for (let b = a + 1; b < placed.length; b++) {
        const pa = placed[a];
        const pb = placed[b];
        const min = pa.r + pb.r + 6;
        const dx = pb.x - pa.x;
        const dy = pb.y - pa.y;
        const d = Math.hypot(dx, dy) || 0.01;
        if (d < min) {
          const push = (min - d) / 2;
          const ux = dx / d;
          const uy = dy / d;
          pa.x -= ux * push;
          pa.y -= uy * push;
          pb.x += ux * push;
          pb.y += uy * push;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
  return { placed, center };
};

export const CitationGraphPanel = ({
  data,
  loading,
  error,
  onRecenter,
}: {
  data: SubgraphResponse | null;
  loading: boolean;
  error: string | null;
  /** Re-run the graph centered on a clicked node. */
  onRecenter: (paperId: string) => void;
}) => {
  const [hoverId, setHoverId] = useState<number | null>(null);
  const [mode, setMode] = useState<LayoutMode>("semantic");

  const semanticAvailable = !!data?.semantic;
  const activeMode: LayoutMode =
    mode === "semantic" && semanticAvailable ? "semantic" : "radial";

  const { placed, centerPos } = useMemo(() => {
    if (!data) return { placed: [] as PlacedNode[], centerPos: { x: CX, y: CY } };
    // A node can be both citer and reference (rare); show it once,
    // on the incoming side.
    const seen = new Set<number>();
    const incoming = data.incoming.nodes.filter((n) => {
      if (seen.has(n.id)) return false;
      seen.add(n.id);
      return true;
    });
    const outgoing = data.outgoing.nodes.filter((n) => {
      if (seen.has(n.id)) return false;
      seen.add(n.id);
      return true;
    });
    if (activeMode === "semantic" && data.semantic) {
      const { placed, center } = placeSemantic(
        incoming,
        outgoing,
        data.semantic.coords,
        data.center.id,
      );
      return { placed, centerPos: center };
    }
    return {
      placed: [...placeRadial(incoming, "in"), ...placeRadial(outgoing, "out")],
      centerPos: { x: CX, y: CY },
    };
  }, [data, activeMode]);

  const byId = useMemo(() => {
    const m = new Map<number, PlacedNode>();
    placed.forEach((p) => m.set(p.node.id, p));
    return m;
  }, [placed]);

  const crossEdges = useMemo(
    () =>
      (data?.cross_edges ?? [])
        .map(([src, dst]) => {
          const a = byId.get(src);
          const b = byId.get(dst);
          return a && b ? { src, dst, a, b } : null;
        })
        .filter((e): e is NonNullable<typeof e> => e !== null),
    [data, byId],
  );

  const touchesHover = (src: number, dst: number) =>
    hoverId !== null && (src === hoverId || dst === hoverId);

  /** Slightly bowed quadratic path between two neighbor nodes. */
  const crossPath = (a: PlacedNode, b: PlacedNode) => {
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const cx = mx + (centerPos.x - mx) * 0.25;
    const cy = my + (centerPos.y - my) * 0.25;
    return `M ${a.x} ${a.y} Q ${cx} ${cy} ${b.x} ${b.y}`;
  };

  if (!data && !loading && !error) {
    return (
      <section
        aria-label="Citation graph"
        className="flex min-h-[420px] flex-col items-center justify-center rounded-[3px] border border-zinc-200 bg-white p-8 text-center"
      >
        <Network className="mb-3 h-8 w-8 text-zinc-300" aria-hidden />
        <p className="text-sm font-medium text-zinc-600">
          Pick a paper and run to draw its citation neighborhood
        </p>
        <p className="mt-1 max-w-sm text-xs text-zinc-400">
          Citers on the left, references on the right, cross-citations
          between them — live from the 2.1B-edge graph. Click any node to
          re-center.
        </p>
      </section>
    );
  }

  const hovered = hoverId !== null ? byId.get(hoverId) : null;
  const hoveredSim =
    hovered && data?.semantic
      ? data.semantic.sims[String(hovered.node.id)]
      : undefined;
  const unembedded = placed.filter((p) => !p.embedded).length;

  return (
    <section
      aria-label="Citation graph"
      className="relative overflow-hidden rounded-[3px] border border-zinc-200 bg-white"
    >
      {data && (
        <header className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-zinc-100 px-4 py-3">
          <h2 className="min-w-0 flex-1 truncate text-base leading-7 text-zinc-900">
            {data.center.title || data.key}
          </h2>
          <p className="shrink-0 text-2xs tabular-nums text-zinc-500">
            <span style={{ color: COLOR_IN }}>
              {data.incoming.nodes.length} of {data.incoming.total} citers
            </span>
            {" · "}
            <span style={{ color: COLOR_OUT }}>
              {data.outgoing.nodes.length} of {data.outgoing.total}{" "}
              references
            </span>
            {" · "}
            <span className="text-zinc-400">
              {crossEdges.length} cross-citations
            </span>
          </p>
          <div
            role="group"
            aria-label="Graph layout"
            className="flex shrink-0 rounded-[3px] border border-zinc-200 bg-zinc-100 p-0.5"
          >
            {(
              [
                ["semantic", "Semantic"],
                ["radial", "Radial"],
              ] as const
            ).map(([id, label]) => (
              <button
                key={id}
                type="button"
                aria-pressed={activeMode === id}
                disabled={id === "semantic" && !semanticAvailable}
                title={
                  id === "semantic"
                    ? semanticAvailable
                      ? "Position by abstract embeddings: closer = more similar"
                      : "No abstract embeddings available for this neighborhood"
                    : "Citers left, references right"
                }
                onClick={() => setMode(id)}
                className={cn(
                  "rounded px-2 py-0.5 text-2xs font-medium transition-colors",
                  activeMode === id
                    ? "bg-white text-zinc-900 "
                    : "text-zinc-500 hover:text-zinc-800",
                  id === "semantic" &&
                  !semanticAvailable &&
                  "cursor-not-allowed opacity-40",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </header>
      )}

      {loading && (
        <div
          role="status"
          className="absolute inset-0 z-10 flex items-center justify-center bg-white/70"
        >
          <p className="animate-pulse text-sm text-zinc-500">
            Walking the graph…
          </p>
        </div>
      )}

      {error && !loading ? (
        <div className="flex min-h-[420px] items-center justify-center p-8">
          <p className="max-w-sm text-center text-sm text-red-700" role="alert">{error}</p>
        </div>
      ) : (
        data && (
          <svg
            viewBox={`0 0 ${W} ${H}`}
            role="img"
            aria-label={`Citation graph for ${data.center.title}: ${data.incoming.nodes.length} citing papers, ${data.outgoing.nodes.length} referenced papers, ${crossEdges.length} cross-citation edges`}
            className="block h-auto w-full select-none"
          >
            <defs>
              <marker
                id="arrow-cross"
                viewBox="0 0 8 8"
                refX="7"
                refY="4"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M0 0 L8 4 L0 8 z" fill={COLOR_CROSS} />
              </marker>
              <marker
                id="arrow-hot"
                viewBox="0 0 8 8"
                refX="7"
                refY="4"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M0 0 L8 4 L0 8 z" fill="#f59e0b" />
              </marker>
            </defs>

            {/* center spokes: citer -> center, center -> reference */}
            {placed.map((p) => {
              const dim = hoverId !== null && p.node.id !== hoverId;
              const stroke = p.side === "in" ? COLOR_IN : COLOR_OUT;
              const from = p.side === "in" ? p : centerPos;
              const to = p.side === "in" ? centerPos : p;
              return (
                <line
                  key={`spoke-${p.node.id}`}
                  x1={from.x}
                  y1={from.y}
                  x2={to.x}
                  y2={to.y}
                  stroke={stroke}
                  strokeWidth={p.node.id === hoverId ? 2 : 1.1}
                  strokeOpacity={dim ? 0.08 : 0.4}
                />
              );
            })}

            {/* NxN cross-citations among the displayed neighbors */}
            {crossEdges.map(({ src, dst, a, b }) => {
              const hot = touchesHover(src, dst);
              const dim = hoverId !== null && !hot;
              return (
                <path
                  key={`cross-${src}-${dst}`}
                  d={crossPath(a, b)}
                  fill="none"
                  stroke={hot ? "#f59e0b" : COLOR_CROSS}
                  strokeWidth={hot ? 1.8 : 1}
                  strokeOpacity={dim ? 0.06 : hot ? 0.95 : 0.4}
                  markerEnd={hot ? "url(#arrow-hot)" : "url(#arrow-cross)"}
                />
              );
            })}

            {/* center node */}
            <g>
              <circle
                cx={centerPos.x}
                cy={centerPos.y}
                r={17}
                fill={COLOR_CENTER}
              />
              <text
                x={centerPos.x}
                y={centerPos.y + 34}
                textAnchor="middle"
                className="fill-zinc-900"
                fontSize={11.5}
                fontWeight={600}
              >
                {truncate(data.center.title || data.key, 40)}
              </text>
              <text
                x={centerPos.x}
                y={centerPos.y + 48}
                textAnchor="middle"
                className="fill-zinc-500"
                fontSize={9.5}
              >
                {[data.center.year, `${data.center.count} citations`]
                  .filter(Boolean)
                  .join(" · ")}
              </text>
            </g>

            {/* neighbor nodes */}
            {placed.map((p) => {
              const id = subgraphNodeId(p.node);
              const isHover = p.node.id === hoverId;
              const dimmed =
                hoverId !== null &&
                !isHover &&
                !crossEdges.some(
                  (e) =>
                    (e.src === hoverId && e.dst === p.node.id) ||
                    (e.dst === hoverId && e.src === p.node.id),
                );
              const color = p.side === "in" ? COLOR_IN : COLOR_OUT;
              const labelLeft = p.x < centerPos.x;
              const labelX = labelLeft ? p.x - p.r - 5 : p.x + p.r + 5;
              return (
                <g
                  key={p.node.id}
                  role={id ? "button" : undefined}
                  tabIndex={id ? 0 : undefined}
                  aria-label={`${p.node.title || "Untitled"} (${p.node.year}, ${p.node.count} citations) — press Enter to re-center`}
                  className={id ? "cursor-pointer" : undefined}
                  opacity={dimmed ? 0.3 : 1}
                  onMouseEnter={() => setHoverId(p.node.id)}
                  onMouseLeave={() => setHoverId(null)}
                  onFocus={() => setHoverId(p.node.id)}
                  onBlur={() => setHoverId(null)}
                  onClick={() => id && onRecenter(id)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && id) onRecenter(id);
                  }}
                >
                  <circle
                    cx={p.x}
                    cy={p.y}
                    r={p.r + (isHover ? 2 : 0)}
                    fill={color}
                    fillOpacity={isHover ? 1 : p.embedded ? 0.85 : 0.45}
                    stroke="#fff"
                    strokeWidth={1.5}
                    strokeDasharray={p.embedded ? undefined : "2 2"}
                  />
                  <text
                    x={labelX}
                    y={p.y + 3}
                    textAnchor={labelLeft ? "end" : "start"}
                    fontSize={9.5}
                    className="fill-zinc-600"
                  >
                    {truncate(p.node.title || "Untitled", 26)}
                  </text>
                </g>
              );
            })}
          </svg>
        )
      )}

      {/* hover card */}
      {hovered && (
        <div
          className="pointer-events-none absolute bottom-12 left-4 right-4 z-10 mx-auto max-w-md rounded-[3px] border border-zinc-200 bg-white/95 px-3 py-2 "
          role="status"
        >
          <p className="text-xs font-medium text-zinc-900">
            {hovered.node.title || "Untitled"}
          </p>
          <p className="mt-0.5 text-2xs text-zinc-500">
            {[
              hovered.node.year,
              `${hovered.node.count} citations`,
              hovered.side === "in"
                ? "cites the selected paper"
                : "cited by the selected paper",
              hoveredSim !== undefined
                ? `abstract similarity ${hoveredSim.toFixed(2)}`
                : activeMode === "semantic"
                  ? "no abstract embedding"
                  : null,
            ]
              .filter(Boolean)
              .join(" · ")}
            {" — click to re-center"}
          </p>
        </div>
      )}

      {data && (
        <footer className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-zinc-100 px-4 py-2 text-2xs text-zinc-500">
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: COLOR_IN }}
            />
            cites this paper
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-2.5 w-2.5 rounded-full"
              style={{ background: COLOR_OUT }}
            />
            cited by this paper
          </span>
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-0.5 w-4"
              style={{ background: COLOR_CROSS }}
            />
            cross-citation (arrow points at the cited paper)
          </span>
          <span className="ml-auto">
            {activeMode === "semantic"
              ? `closer = more similar abstracts (cosine)${unembedded > 0
                ? ` · ${unembedded} without embeddings on outer ring`
                : ""
              }`
              : "node size = citation count"}
          </span>
        </footer>
      )}
    </section>
  );
};
